/* eslint-disable react-refresh/only-export-components */
import { lazy } from 'react'
import { Navigate } from 'react-router-dom'

const AuthGuard = lazy(() => import('src/guards/AuthGuard'))
const GuestGuard = lazy(() => import('src/guards/GuestGuard'))
const NotForClientsGuard = lazy(() => import('src/guards/NotForClientsGuard'))
const GuestLayout = lazy(() => import('src/layouts/GuestLayout'))
const DashboardLayout = lazy(() => import('src/layouts/DashboardLayout'))
const WelcomeLayout = lazy(() => import('src/layouts/WelcomeLayout'))
const Transactions = lazy(() => import('src/views/Transactions'))
const Deposit = lazy(() => import('src/views/Deposit'))
const Account = lazy(() => import('src/views/Account'))
const Documents = lazy(() => import('src/views/Documents'))
const Terms = lazy(() => import('src/views/Terms'))
const Contact = lazy(() => import('src/views/Contact'))
const Welcome = lazy(() => import('src/views/Welcome'))
const SignIn = lazy(() => import('src/views/login'))
const ResetPassword = lazy(() => import('src/views/login/ResetPassword'))
const Source = lazy(() => import('src/views/Source/Source'))
const SelectAccount = lazy(() => import('src/views/SelectAccount'))
const NewAccount = lazy(() => import('src/views/NewAccount'))
const Bills = lazy(() => import('src/views/Bills'))
const Statements = lazy(() => import('src/views/Statements'))
const NewBill = lazy(() => import('src/views/Bills/NewBill'))
const UserProfile = lazy(() => import('src/views/UserProfile'))
const SecurityLayout = lazy(() => import('src/views/UserProfile/Security'))
const MfaSettings = lazy(() => import('src/views/UserProfile/Security/MfaSettings'))
const EditRecurringBill = lazy(() => import('src/views/Bills/BillsList/EditRecurringBill'))
const ResetProfilePassword = lazy(() => import('src/views/UserProfile/ResetProfilePassword'))
// Disabled: Applications / Application Auto Pays / Notary Requests QB tables aren't used
// const Enrollments = lazy(() => import('src/views/Enrollment/Enrollments'))
// const ApplicationLayout = lazy(() => import('src/layouts/ApplicationLayout'))
// const Application = lazy(() => import('src/views/Application'))
// import Enroll from 'src/views/Enroll'
const Register = lazy(() => import('src/views/Register'))
const FourOhFour = lazy(() => import('src/components/404'))
import HomePage from '../views/Frontend/HomePage'
const SetPassword = lazy(() => import('src/views/login/SetPassword'))
const MfaVerification = lazy(() => import('src/views/login/MfaVerification'))
const ChooseMfa = lazy(() => import('src/views/login/ChooseMfa'))
const AddPhone = lazy(() => import('src/views/login/AddPhone'))
const SetupTotp = lazy(() => import('src/views/login/SetupTotp'))
const NewPassword = lazy(() => import('src/views/login/NewPassword'))
const ExpiredPassword = lazy(() => import('src/views/login/ExpiredPassword'))
const LegacySetPasswordLink = lazy(() => import('src/views/login/LegacySetPasswordLink'))
const PublicLayout = lazy(() => import('../views/Frontend/layout'))
const ContactUs = lazy(() => import('../views/Frontend/ContactUs'))
const BlogList = lazy(() => import('../views/Frontend/Blog'))
const BlogPost = lazy(() => import('../views/Frontend/Blog/BlogPost'))
const BlogRedirect = lazy(() => import('../views/Frontend/Blog/BlogRedirect'))
const SpecialNeedsTrust = lazy(() => import('src/views/Frontend/TypesOfTrusts/SpecialNeeds'))
const Estate = lazy(() => import('src/views/Frontend/TypesOfTrusts/Estate'))
const MedicaidAssetProtectionTrust = lazy(() => import('src/views/Frontend/TypesOfTrusts/MedicaidAssetProtection'))

const routes = [
    {
        element: <PublicLayout />,
        children: [
            {
                path: '/',
                element: <HomePage />
            },
            {
                path: '/contact',
                element: <ContactUs />
            },
            {
                path: '/discover',
                element: <BlogList />
            },
            {
                path: '/discover/:slug',
                element: <BlogPost />
            },
            // Note to future self: I added these redirects for /blog to /discover on 06/14/2024.
            // if we switch back to /blog later, we can just remove these redirects.
            {
                path: '/blog',
                element: <Navigate to="/discover" replace />
            },
            {
                path: '/blog/:slug',
                element: <BlogRedirect />
            },
            {
                path: '/types-of-trusts/special-needs-trust-administration',
                element: <SpecialNeedsTrust />
            },
            {
                path: '/types-of-trusts/trust-and-estate-planning-services',
                element: <Estate />
            },
            {
                path: '/types-of-trusts/medicaid-asset-protection-trust',
                element: <MedicaidAssetProtectionTrust />
            }
        ]
    },

    {
        path: 'auth',
        element: (
            <GuestGuard>
                <GuestLayout />
            </GuestGuard>
        ),
        children: [
            {
                path: 'reset-password',
                element: <ResetPassword />
            },
            {
                path: 'login',
                element: <SignIn />
            },
            {
                path: 'choose-mfa',
                element: <ChooseMfa />
            },
            {
                path: 'add-phone',
                element: <AddPhone />
            },
            {
                path: 'setup-totp',
                element: <SetupTotp />
            },
            {
                path: 'mfa-verification',
                element: <MfaVerification />
            },
            {
                path: 'set-password',
                element: <SetPassword />
            },
            {
                // Links in emails sent by the old portal.
                path: 'setpassword/:formtype/:token',
                element: <LegacySetPasswordLink />
            },
            {
                path: 'expired-password',
                element: <ExpiredPassword />
            },
            {
                path: 'reset-password/new',
                element: <NewPassword />
            },
            // {
            //   path: 'enroll',
            //   element: <Enroll />
            // },
            {
                path: 'register',
                element: <Register />
            }
        ]
    },
    {
        element: (
            <AuthGuard>
                <DashboardLayout />
            </AuthGuard>
        ),
        children: [
            {
                path: '/transactions/:id',
                element: <Transactions />
            },
            {
                path: '/deposit/:id',
                element: <Deposit />
            },
            {
                path: '/bills/:id',
                element: (
                    <NotForClientsGuard>
                        <Bills />
                    </NotForClientsGuard>
                )
            },
            {
                path: '/bills/:id/new',
                element: (
                    <NotForClientsGuard>
                        <NewBill />
                    </NotForClientsGuard>
                )
            },
            {
                path: '/bills/:id/edit',
                element: (
                    <NotForClientsGuard>
                        <EditRecurringBill />
                    </NotForClientsGuard>
                )
            },
            {
                path: '/account/:id',
                element: <Account />
            },
            {
                path: '/statements/:id',
                element: (
                    <NotForClientsGuard>
                        <Statements />
                    </NotForClientsGuard>
                )
            },
            {
                path: '/documents/:id',
                element: <Documents />
            },
            {
                path: '/terms/:id',
                element: <Terms />
            },
            {
                path: '/contact/:id',
                element: <Contact />
            }
        ]
    },
    {
        element: (
            <AuthGuard>
                <WelcomeLayout />
            </AuthGuard>
        ),
        children: [
            {
                path: '/select-source',
                element: <Source />
            },
            {
                path: '/select-account',
                element: <SelectAccount />,
                children: [
                    {
                        path: '/select-account/:id',
                        element: <SelectAccount />
                    }
                ]
            },
            {
                path: '/new-account',
                element: <NewAccount />
            },
            {
                path: '/my-profile',
                element: <UserProfile />
            },
            {
                path: '/my-profile/security',
                element: <SecurityLayout />,
                children: [
                    { index: true, element: <MfaSettings /> }, // /my-profile/security
                    { path: 'password', element: <ResetProfilePassword /> }, // /my-profile/security/password
                    { path: 'add-phone', element: <AddPhone /> }, // in-app add phone
                    { path: 'setup-totp', element: <SetupTotp /> }, // in-app TOTP setup
                    { path: 'verify', element: <MfaVerification /> } // <— in-app verify
                ]
            },
            // {
            //   path: '/my-profile/mfa-verification',
            //   element: <MfaVerification />
            // },
            // keep an alias/redirect from the old URL for safety
            {
                path: '/my-profile/update-password',
                element: <Navigate to="/my-profile/security/password" replace />
            },
            // Disabled: Applications QB table isn't used
            // {
            //     path: '/enrollments',
            //     element: <Enrollments />
            // },
            {
                path: '/welcome',
                element: <Welcome />
            },
            {
                path: '/welcome/:id',
                element: <Welcome />
            }
        ]
    },
    // Disabled: Applications / Application Auto Pays / Notary Requests QB tables aren't used
    // {
    //     element: (
    //         <AuthGuard>
    //             <ApplicationLayout />
    //         </AuthGuard>
    //     ),
    //     children: [
    //         {
    //             path: '/application/:id',
    //             element: <Application />
    //         }
    //     ]
    // },
    {
        element: <FourOhFour />,
        path: '*'
    }
    // {
    //   path: '/auth/enroll',
    //   element: (
    //     <WelcomeWrapper hideProfile>
    //       <Enroll />
    //     </WelcomeWrapper>
    //   )
    // }
]
export default routes
